import React, { useRef, useEffect, useState, useCallback } from 'react';
import styles from './Chart.module.css';
import { HeartRateIcon } from '../../../components/icons/Icons';
import { useSelector } from 'react-redux';
import PerinComposedChart from './PerinComposedChart';
import { getLatestMeasurement } from '../../../utils/unitUtils';
import VitalCardHeader from './components/VitalCardHeader';

const GlucoseLevelCardView = React.forwardRef(({ className, healthEvaluationData, vitalName, handlePointClick, alertsForPeriod }, ref) => {
  const chartContainerRef = useRef(null);
  const userUnitPreference = useSelector((state) => state.userInfo.unitType);
  const { startDate, endDate, averageGrouped, readingInterval } = useSelector((state) => state.patientDetailFilter);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
 const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const [opacity, setOpacity] = useState({});
  const [highestMeasurement, setHighestMeasurement] = useState(null);
  const [lowestMeasurement, setLowestMeasurement] = useState(null);

  const updateData = useCallback(() => {
    // Group data by date and deviceName
    const groupedData = healthEvaluationData.reduce((acc, entry) => {
      const date = entry.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][entry.deviceName] = entry;
      return acc;
    }, {});

    let transformedData = Object.values(groupedData);
    transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    setData(transformedData);
    const deviceOpacity = healthEvaluationData.reduce((acc, entry) => {
      const { deviceName } = entry;

      if (!acc[deviceName]) {
        acc[deviceName] = [];
      }
      acc[deviceName].push(entry);

      return acc;
    }, {});

    setOpacity(Object.keys(deviceOpacity).reduce((acc, key) => {
      acc[key] = 1;
      return acc;
    }, {}));
    const calculateHighestAndLowest = (data) => {
      if (data.length === 0) return; // Early return if no data

      let highest = null;
      let lowest = null;

      deviceNames.forEach((deviceName) => {
        data.forEach(entry => {
          if (entry[deviceName] && entry[deviceName][vitalName]!==null) {
            if (highest === null || entry[deviceName][vitalName] > highest) {
              highest = entry[deviceName][vitalName];
            }
            if (lowest === null || entry[deviceName][vitalName] < lowest) {
              lowest = entry[deviceName][vitalName];
            }
          }
        });
      })

      setHighestMeasurement(highest);
      setLowestMeasurement(lowest);
    };

    calculateHighestAndLowest(transformedData);
    const latestMeasurementData = getLatestMeasurement(
      healthEvaluationData,
      vitalName,
      averageGrouped,
      userUnitPreference,
      vitals,
      0
    );
    setDisplayData(latestMeasurementData);
  }, [healthEvaluationData]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const handleLegendClick = (e) => {
    const deviceName = e.value;
    setOpacity((op) => ({ ...op, [deviceName]: op[deviceName] === 1 ? 0 : 1 }));
  };

  const deviceNames = Array.from(new Set(healthEvaluationData.map(entry => entry.deviceName)));

  const deviceNamesData = deviceNames.filter(deviceName =>
    healthEvaluationData.some(entry => entry.deviceName === deviceName && entry.glucoseLevel !== null)
  );

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={HeartRateIcon}
        vitalName={"Glucose"}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
        outOfRangeCount={displayData.outOfRangeCount}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <PerinComposedChart
          vitalName={vitalName}
          data={data}
          vitals={vitals}
          deviceNamesData={deviceNamesData}
          opacity={opacity}
          handleLegendClick={handleLegendClick}
          handlePointClick={(deviceName, payload) => handlePointClick(deviceName, payload, vitals)}
          highestMeasurement={highestMeasurement}
          lowestMeasurement={lowestMeasurement}
          alertsForPeriod={alertsForPeriod}
        />
      </div>
    </div>
  );
});

export default GlucoseLevelCardView;
